import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { Typography, Grid, Box, makeStyles } from "@material-ui/core";
import translate from "i18n/translate";
import { AUTH_TITLE_SPACE, AUTH_CONTAINER_WIDTH } from "../../../constants";
import {
  AUTH_PHONE_VALIDATION,
  AUTH_PHONE_VALIDATION_SEND_CODE,
} from "constants/api";
import { PORTAL_LAYOUT_ROUTE } from "routes/portal/portalPaths";
import { SrButton, SrInput, SrContentWrapper } from "../../common";
import { phoneValidatedSuccess } from "redux/auth/actions";

const initialValues = {
  code: "",
};

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: AUTH_CONTAINER_WIDTH,
  },
  buttonsMargin: {
    "& > button": {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    "& > button:first-child": {
      marginLeft: 0,
    },
    "& > button:last-child": {
      marginRight: 0,
    },
  },
  formWidth: {
    width: "100%",
  },
}));

const validationSchema = (message) =>
  Yup.object().shape({
    code: Yup.string()
      .test(
        "len",
        message.lenghtMessage,
        (val) => !val || (val && val.toString().length === 6)
      )
      .required(message.requiredMessage),
  });

const makeCodeRequest = async (code) => {
  const ssoTokenString = localStorage.getItem("sso");
  const ssoToken = ssoTokenString && JSON.parse(ssoTokenString);

  try {
    const url = AUTH_PHONE_VALIDATION.replace("{code}", code);

    const params = { ssoToken };

    const response = await axios.get(url, { params });

    return response;
  } catch (error) {
    return error;
  }
};

const PhoneVerification = () => {
  const publicIp = useSelector(({ auth }) => auth.publicIp);
  const dispatch = useDispatch();
  const classes = useStyles();
  const intl = useIntl();
  const history = useHistory();
  const [loading] = useState(true);
  const [resendCodeIsSubmitting, setResendCodeIsSubmitting] = useState(false);

  const makeResendCodeRequest = async () => {
    try {
      const response = await axios.post(AUTH_PHONE_VALIDATION_SEND_CODE, {
        ip: publicIp,
      });
      return response;
    } catch (error) {
      return error;
    }
  };

  const onSubmit = async (values) => {
    const result = await makeCodeRequest(values.code);

    if (result?.data?.success) {
      toast.success(intl.formatMessage({ id: "auth.validationSuccess" }));
      dispatch(phoneValidatedSuccess());
      history.push(PORTAL_LAYOUT_ROUTE);
    } else {
      toast.error(intl.formatMessage({ id: "auth.validationError" }));
    }
  };

  const resendCode = async () => {
    setResendCodeIsSubmitting(true);

    const result = await makeResendCodeRequest();

    if (result?.data?.success) {
      toast.success(intl.formatMessage({ id: "auth.sendCodeSuccess" }));
    } else {
      const responseData = result?.response?.data;

      if (responseData.success === false) {
        toast.error(responseData?.error?.description);
      } else {
        toast.error(intl.formatMessage({ id: "auth.sendCodeError" }));
      }
    }

    setResendCodeIsSubmitting(false);
  };

  return (
    <Grid container justifyContent="center">
      <Grid
        container
        item
        xs={12}
        justifyContent="center"
        className={classes.container}
      >
        <Grid container item xs={12} justifyContent="center">
          <Box mb={AUTH_TITLE_SPACE}>
            <Typography variant="h2">
              {translate("auth.phoneVerification")}
            </Typography>
          </Box>
        </Grid>
        <SrContentWrapper isLoading={loading}>
          <Grid container item xs={12} justifyContent="center">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema({
                lenghtMessage: intl.formatMessage({ id: "auth.codeLenght" }),
                requiredMessage: intl.formatMessage({
                  id: "auth.codeRequired",
                }),
              })}
              validateOnChange="true"
              onSubmit={onSubmit}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                touched,
                values,
                isSubmitting,
              }) => (
                <Form noValidate className={classes.formWidth}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <SrInput
                        variant="outlined"
                        required
                        fullWidth
                        error={Boolean(errors.code && touched.code)}
                        helperText={touched.code && errors.code}
                        id="code"
                        type="text"
                        name="code"
                        autoComplete="code"
                        label={translate("auth.phoneVerificationCode")}
                        className="form-control"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.code}
                        inputProps={{ maxLength: 6 }}
                      />
                    </Grid>
                    <Grid item xs={12} className={classes.buttonsMargin}>
                      <SrButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                      >
                        {translate("common.verify")}
                      </SrButton>
                      <SrButton
                        variant="contained"
                        onClick={resendCode}
                        disabled={resendCodeIsSubmitting || isSubmitting}
                      >
                        {translate("auth.codeNotRecived")}
                      </SrButton>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Grid>
        </SrContentWrapper>
      </Grid>
    </Grid>
  );
};
export default PhoneVerification;
